import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, memo, useMemo } from 'react';
import { NumericFormat } from 'react-number-format';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { StyledInputContainer } from '../common';
import { ALLOWED_DECIMAL_SEPARATORS, DEFAULT_MAX, DEFAULT_MIN, DEFAULT_TRANSLATIONS } from './constants';
import { toPrecision } from './helpers';
import { ReStyledInput as StyledInput } from './styled';
import {} from './types';
export const NumberInput = memo(forwardRef(function NumberInput({ value, onChange, min = DEFAULT_MIN, max = DEFAULT_MAX, minExclusive = false, maxExclusive = false, success, warning, error, width, dataTestId, decimalPlaces = 0, onBlur, disabled, translations, ...props }, ref) {
    const mergedTranslations = useMemo(() => ({
        ...DEFAULT_TRANSLATIONS,
        ...translations,
    }), [translations]);
    const placeholder = useMemo(() => {
        const gtSign = minExclusive ? '>' : '≥';
        const ltSign = maxExclusive ? '<' : '≤';
        return `${min} ${gtSign} ${mergedTranslations.value} ${ltSign} ${max}`;
    }, [max, maxExclusive, mergedTranslations.value, min, minExclusive]);
    const clampValue = useCallbackRef((newValue) => {
        if (newValue === null || newValue === undefined)
            return null;
        if (minExclusive ? newValue <= min : newValue < min) {
            return minExclusive ? min + 10 ** -decimalPlaces : min;
        }
        if (maxExclusive ? newValue >= max : newValue > max) {
            return maxExclusive ? max - 10 ** -decimalPlaces : max;
        }
        return toPrecision(newValue, decimalPlaces);
    });
    const onBlurHandler = useCallbackRef((event) => {
        onChange?.(clampValue(value));
        onBlur?.(event);
    });
    const handleChange = useCallbackRef((values) => {
        onChange?.(values.floatValue ?? null);
    });
    const handlePaste = useCallbackRef((e) => {
        const pastedText = e.clipboardData.getData('text');
        // Check if pasted text contains comma and no dot (comma as decimal separator)
        if (pastedText.includes(',') && !pastedText.includes('.')) {
            e.preventDefault();
            // Get the input element and current selection
            const input = e.target;
            const start = input.selectionStart || 0;
            const end = input.selectionEnd || 0;
            const currentValue = input.value || '';
            // Replace comma with dot in pasted text
            const normalizedPastedText = pastedText.replace(',', '.');
            // Construct new value by inserting normalized text at cursor position
            const newValue = currentValue.substring(0, start) + normalizedPastedText + currentValue.substring(end);
            // Parse and update the value
            const parsedValue = parseFloat(newValue);
            if (!Number.isNaN(parsedValue)) {
                onChange?.(parsedValue);
            }
        }
        // Call original onPaste if provided
        props.onPaste?.(e);
    });
    // This is a workaround to fix the issue with the input value not being cleared when the value is null or undefined.
    // https://github.com/s-yadav/react-number-format/issues/727
    const numericFormatValue = value ?? '';
    return (_jsx(StyledInputContainer, { width: width, "$inputDisabled": props.readOnly || disabled, "$success": success, "$warning": warning, "$error": error, "data-testid": dataTestId, children: _jsx(NumericFormat, { customInput: StyledInput, value: numericFormatValue, onValueChange: handleChange, onPaste: handlePaste, onBlur: onBlurHandler, disabled: disabled, ...props, placeholder: props.placeholder ?? placeholder, getInputRef: ref, allowNegative: min < 0, decimalScale: decimalPlaces, allowedDecimalSeparators: ALLOWED_DECIMAL_SEPARATORS }) }));
}));
